/**
 * Decomposing a desired playback rate into deck controls.
 *
 * Pure, and separated from Deck specifically so it can be tested: getting this
 * wrong produces a deck playing at a plausible-but-incorrect tempo, which is
 * very hard to distinguish by ear from ordinary beat drift.
 */

export interface RateSetting {
  /** Half, normal or double time. */
  octave: number;
  /** Index into the available tempo fader ranges. */
  rangeIndex: number;
  /** Fader position, -1..1. */
  fader: number;
  /** The rate these settings actually produce. */
  achieved: number;
  /** False when the request was outside every available range and was clamped. */
  exact: boolean;
}

/**
 * Find the octave, fader range and fader position producing `total`.
 *
 * Picks the narrowest range that can express the request, because a narrow
 * range gives finer control per pixel of fader travel; only widens when it has
 * to. If no range reaches, the result is clamped and `exact` is false — callers
 * must check that rather than assume they got what they asked for.
 */
export function decomposeRate(total: number, ranges: readonly number[]): RateSetting {
  const target = Math.max(0.1, Math.min(total, 4));

  let octave = 1;
  for (const candidate of [0.5, 1, 2]) {
    if (Math.abs(target / candidate - 1) < Math.abs(target / octave - 1)) {
      octave = candidate;
    }
  }

  const deviation = target / octave - 1;

  for (let index = 0; index < ranges.length; index++) {
    const range = ranges[index] as number;
    if (Math.abs(deviation) <= range + 1e-9) {
      const fader = range === 0 ? 0 : deviation / range;
      return {
        octave,
        rangeIndex: index,
        fader: Math.max(-1, Math.min(1, fader)),
        achieved: (1 + fader * range) * octave,
        exact: true,
      };
    }
  }

  // Out of reach even at the widest setting: clamp, and say so.
  const widestIndex = Math.max(0, ranges.length - 1);
  const widest = (ranges[widestIndex] as number) ?? 0;
  const fader = Math.max(-1, Math.min(1, widest === 0 ? 0 : deviation / widest));
  return {
    octave,
    rangeIndex: widestIndex,
    fader,
    achieved: (1 + fader * widest) * octave,
    exact: false,
  };
}
